// App.js
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';

// Layout Components
import AdminLayout from './components/AdminLayout';
import AuthLayout from './components/AuthLayout';
import PublicLayout from './components/PublicLayout';

// Protected Route Components
import ProtectedRoute from './components/ProtectedRoute';
import PublicRoute from './components/PublicRoute';

// Auth Pages (public)
import SignIn from './auth/SignIn';
import SignUp from './auth/SignUp';
import ForgotPassword from './auth/ForgotPassword';
import ResetPassword from './auth/ResetPassword';
import EmailVerification from './auth/EmailVerification';
import CompleteApplication from './auth/CompleteApplication';

// Admin Pages
import Dashboard from './pages/admin/Dashboard';
import Profile from './pages/admin/Profile';
import BlogList from './pages/admin/BlogList';
import CreateBlog from './pages/admin/CreateBlog';
import EditBlog from './pages/admin/EditBlog';
import BlogDetail from './pages/admin/BlogDetail';
import ContentList from './pages/admin/ContentList';
import CreateContent from './pages/admin/CreateContent';
import ContactControl from './pages/admin/ContactControl';
import AdminApprovals from './pages/admin/AdminApprovals';
import Settings from './pages/admin/Settings';
import UserControl from './pages/admin/UserControl';
import ContentDetail from './pages/admin/ContentDetail';
import EditContent from './pages/admin/EditContent';

// Public Pages
import Home from './pages/Home';
import About from './pages/About';
import Contact from './pages/Contact';

import './App.css';

function App() {
  return (
    <ThemeProvider>

      <AuthProvider>
        <Router>
          <Routes>
            {/* Public Routes - Redirect if authenticated */}
            <Route element={<PublicLayout />}>
              <Route path="/" element={
                <PublicRoute>
                  <Home />
                </PublicRoute>
              } />
              <Route path="/about" element={
                <PublicRoute>
                  <About />
                </PublicRoute>
              } />
              <Route path="/contact" element={
                <PublicRoute>
                  <Contact />
                </PublicRoute>
              } />
            </Route>

            {/* Auth Routes - Redirect to admin if already authenticated */}
            <Route element={<AuthLayout />}>
              <Route path="/auth/sign-in" element={
                <PublicRoute>
                  <SignIn />
                </PublicRoute>
              } />
              <Route path="/auth/sign-up" element={
                <PublicRoute>
                  <SignUp />
                </PublicRoute>
              } />
              <Route path="/auth/forgot-password" element={
                <PublicRoute>
                  <ForgotPassword />
                </PublicRoute>
              } />
              <Route path="/auth/reset-password" element={
                <PublicRoute>
                  <ResetPassword />
                </PublicRoute>
              } />
              <Route path="/auth/verify-email" element={<EmailVerification />} />
              <Route path="/auth/complete-application" element={<CompleteApplication />} />
            </Route>

            {/* Admin Routes - With AdminLayout and Protection */}
            <Route 
              path="/admin" 
              element={
                <ProtectedRoute requireAdmin>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              {/* Redirect /admin to /admin/dashboard */}
              <Route index element={<Navigate to="/admin/dashboard" replace />} />
              
              {/* Admin Dashboard Routes - Accessible by both Admin and Super Admin */}
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="profile" element={<Profile />} />
              
              {/* Blog Management - Accessible by both Admin and Super Admin */}
              <Route path="blogs" element={<BlogList />} />
              <Route path="blogs/create" element={<CreateBlog />} />
              <Route path="blogs/edit/:id" element={<EditBlog />} />
              <Route path="blogs/:id" element={<BlogDetail />} />
              
              {/* Content Management */}
              <Route path="content" element={<ContentList />} />
              <Route path="content/create" element={<CreateContent />} />
              <Route path="content/:id" element={<ContentDetail />} />
              <Route path="content/edit/:id" element={<EditContent />} />
              
              {/* Contact Control - Accessible by both Admin and Super Admin */}
              <Route path="contact-control" element={<ContactControl />} />
              <Route path="settings" element={<Settings />} />
              
              {/* Super Admin Only Routes - Full user management */}
              <Route 
                path="users" 
                element={
                  <ProtectedRoute requireSuperAdmin>
                    <UserControl />
                  </ProtectedRoute>
                } 
              />
              
              {/* Super Admin Only Routes - Approvals management */}
              <Route 
                path="approvals" 
                element={
                  <ProtectedRoute requireSuperAdmin>
                    <AdminApprovals />
                  </ProtectedRoute>
                } 
              />
            </Route>

            {/* Fallback Route */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;